import axios from 'axios';
import { store, persistor } from '../redux/store';
import { updateAccessToken, logoutReduxState } from '../redux/slices/authSlice';

const baseURL = import.meta.env.VITE_APP_API_URL || '';
const apiClient = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 1000000000,
});

apiClient.interceptors.request.use(
  config => {
    const token = store.getState().auth.tokens?.access;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => Promise.reject(error)
);

apiClient.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    // Check if error is due to invalid token and request hasn't been retried yet
    if (
      error.response &&
      error.response.status === 401 &&
      error.response.data?.code === 'token_not_valid' &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;

      try {
        // Get refresh token from store
        const refreshToken = store.getState().auth.tokens?.refresh;

        if (!refreshToken) {
          // No refresh token available, redirect to login
          console.log('No refresh token available, redirecting to login...');
          // Clear all persisted storage
          await persistor.purge();
          store.dispatch(logoutReduxState());
          return Promise.reject(error);
        }

        // Call refresh token endpoint
        const response = await axios.post(`${baseURL}/api/token/refresh/`, {
          refresh: refreshToken,
        });

        if (response.data.access) {
          // Update token in store
          store.dispatch(updateAccessToken(response.data.access));

          // Update Authorization header and retry original request
          originalRequest.headers.Authorization = `Bearer ${response.data.access}`;
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        console.log('Token refresh failed, redirecting to login...');
        // Clear all persisted storage
        await persistor.purge();
        store.dispatch(logoutReduxState());
        return Promise.reject(refreshError);
      }
    }

    if (error.response && error.response.status === 401) {
      const base = originalRequest?.baseURL || '';
      const url = originalRequest?.url || '';
      const responseUrl = error?.response?.config?.url || '';
      const requestUrl = `${base}${url}` || responseUrl || url;
      const isLoginRequest =
        requestUrl.includes('/api/token/') ||
        requestUrl.includes('api/token/') ||
        requestUrl.endsWith('/api/token') ||
        requestUrl.endsWith('/api/token/') ||
        requestUrl.endsWith('api/token') ||
        requestUrl.endsWith('api/token/');
      if (isLoginRequest) {
        return Promise.reject(error);
      }

      console.log('Unauthorized, redirecting to login...');
      // Clear all persisted storage
      await persistor.purge();
      store.dispatch(logoutReduxState());
    }

    return Promise.reject(error);
  }
);

export default apiClient;
